/**
 * @fileoverview Platform-agnostic Click&Pledge wallet payment helpers.
 *
 * This module is the single source of truth for the wallet (Apple Pay,
 * Google Pay, PayPal one-time/recurring, Amazon Pay) business logic that is
 * shared between the Web build (components/web/pages/PaymentMethod.js +
 * components/web/utility/gatewayProcessing.js) and the React Native build
 * (components/native/GiventDonateModal.js). It contains ONLY pure functions —
 * no window/document/crypto/DOM access — so it can be imported safely on both
 * web (browser) and native (Hermes) runtimes.
 *
 * The transport layer differs per platform and is intentionally NOT shared:
 *   - Web opens a popup window and listens for postMessage from oauth.html.
 *   - Native opens a react-native-webview and intercepts the
 *     go.meethour.io:// deep-link return from oauth.html.
 * Only the gateway payloads, identifiers and donation broadcast shape are
 * common, and those live here.
 */

/**
 * Generates a random custom id for PayPal (per the official PayPal
 * documentation GUID shape). Mirrors the previous per-platform copies.
 *
 * @returns {string} A GUID-shaped random hexadecimal id.
 */
export function generateCustomId() {
    /**
     * Builds a random uppercase hexadecimal string of the requested length.
     *
     * @param {number} size - The number of hex characters to produce.
     * @returns {string} The random hex string.
     */
    function getRandomHex(size) {
        return [ ...Array(size) ]
            .map(() =>
                Math.floor(Math.random() * 16)
                    .toString(16)
                    .toUpperCase()
            )
            .join('');
    }

    return `${getRandomHex(4)}${getRandomHex(4)}-${getRandomHex(4)}-${getRandomHex(4)}-${getRandomHex(4)}-${getRandomHex(12)}`;
}

/**
 * Resolves the Click&Pledge gateway environment (BZ / TEST / LIVE) from the
 * development flag and the form account mode. LIVE maps to the production
 * (meethour.io) environment.
 *
 * @param {boolean} isDevelopment - Whether the host is a development domain.
 * @param {string} accountMode - The form account mode ('Y' means TEST).
 * @returns {string} The gateway mode key ('BZ', 'TEST' or 'LIVE').
 */
export function resolveCnpAppMode(isDevelopment, accountMode) {
    if (isDevelopment) {
        return 'BZ';
    }

    return accountMode === 'Y' ? 'TEST' : 'LIVE';
}

/**
 * Builds the oauth.html return URL used by the gateway redirect flows. When
 * the caller runs inside a desktop (Electron) or mobile (React Native) shell
 * the app deep-link is appended so oauth.html re-invokes the app via
 * go.meethour.io:// after the gateway redirect.
 *
 * @param {Object} options - The builder options.
 * @param {boolean} options.includeAppDeepLink - Append the app deep-link.
 * @param {string} options.origin - The web origin serving oauth.html.
 * @returns {string} The fully-qualified return URL.
 */
export function buildOAuthReturnUrl({ includeAppDeepLink, origin }) {
    const base = `${origin}/static/oauth.html`;

    if (includeAppDeepLink) {
        return `${base}?app_url=${encodeURIComponent('go.meethour.io://')}`;
    }

    return base;
}

/**
 * Builds the PayPal Create Order payload (one-time donations).
 *
 * @param {Object} options - The builder options.
 * @param {string} options.amount - The total amount as a fixed string (e.g. '10.00').
 * @param {string} options.currencyCode - The ISO currency code (uppercase).
 * @param {string} options.itemName - The donation item name.
 * @param {string} options.itemSKU - The donation item SKU.
 * @param {string} options.orgId - The Click&Pledge organization id.
 * @param {string} options.randomStr - The form key used as the random string.
 * @param {string} options.returnUrl - The oauth.html return/cancel URL.
 * @returns {Object} The payload accepted by the PayPal create-order API.
 */
export function buildPayPalCreateOrderPayload({ amount, currencyCode, itemName, itemSKU, orgId, randomStr, returnUrl }) {
    return {
        orgId,
        intentData: {
            intent: 'CAPTURE',
            purchase_units: [ {
                reference_id: generateCustomId(),
                description: itemName,
                amount: {
                    currency_code: currencyCode,
                    value: amount,
                    breakdown: {
                        item_total: {
                            currency_code: currencyCode,
                            value: amount
                        },
                        shipping: null,
                        tax_total: null
                    }
                },
                items: [ {
                    name: itemName,
                    sku: itemSKU,
                    unit_amount: {
                        currency_code: currencyCode,
                        value: amount
                    },
                    quantity: '1',
                    category: 'DONATION'
                } ],
                shipping: { address: null }
            } ],
            application_context: {
                return_url: returnUrl,
                cancel_url: returnUrl,
                shipping_preference: 'NO_SHIPPING'
            }
        },
        randomStr
    };
}

/**
 * Builds the PayPal recurring billing-agreement token payload.
 *
 * @param {Object} options - The builder options.
 * @param {string} options.orgId - The Click&Pledge organization id.
 * @param {string} options.randomStr - The form key used as the random string.
 * @param {string} options.returnUrl - The oauth.html return/cancel URL.
 * @returns {Object} The payload accepted by the PayPal recurring token API.
 */
export function buildPayPalRecurringPayload({ orgId, randomStr, returnUrl }) {
    return {
        Return_url: returnUrl,
        Cancel_url: returnUrl,
        orgId,
        randomStr
    };
}

/**
 * Builds the Amazon Pay payment-intent payload.
 *
 * @param {Object} options - The builder options.
 * @param {string} options.accountId - The Click&Pledge organization id.
 * @param {number} options.amount - The amount in the smallest currency unit (cents).
 * @param {Object} options.contact - The contact { fname, lname, email }.
 * @param {string} options.ipAddress - The donor IP address.
 * @param {string} options.randomStr - The form key used as the random string.
 * @param {string} options.returnUrl - The oauth.html return URL.
 * @param {string} options.userAgent - The donor user agent string.
 * @returns {Object} The payload accepted by the Amazon Pay intent API.
 */
export function buildAmazonIntentPayload({ accountId, amount, contact, ipAddress, randomStr, returnUrl, userAgent }) {
    return {
        intentData: {
            AccountId: accountId,
            Amount: amount,
            ReturnURL: returnUrl,
            MandateData: {
                IPAddress: ipAddress,
                UserAgent: userAgent
            },
            Contact: {
                FirstName: contact?.fname || '',
                LastName: contact?.lname || '',
                Email: contact?.email || ''
            }
        },
        randomStr
    };
}

/**
 * Builds the donation stream/marquee broadcast payload that is shared with all
 * participants and stored in the donation stream. The anonymous handling and
 * field names match the web and native implementations exactly. The literal
 * 'Anonymous' token is intentionally not translated so every participant sees a
 * consistent value regardless of the donor locale (web parity).
 *
 * @param {Object} options - The builder options.
 * @param {string} options.amount - The display amount (already formatted).
 * @param {string} [options.comments] - The optional public comment.
 * @param {string} [options.dDate] - Optional ISO donation date (native only).
 * @param {string} options.firstName - The donor first name.
 * @param {string} options.gatewayTransactionNumber - The gateway transaction number.
 * @param {boolean} options.isAnonymous - Whether the donor is anonymous.
 * @param {string} options.lastName - The donor last name.
 * @param {string} options.periodicity - The recurring periodicity.
 * @param {string} options.transactionNumber - The transaction/order number.
 * @returns {Object} The donation stream payload.
 */
export function buildDonationStreamData({
    amount,
    comments,
    dDate,
    firstName,
    gatewayTransactionNumber,
    isAnonymous,
    lastName,
    periodicity,
    transactionNumber
}) {
    const streamData = {
        firstName: isAnonymous ? 'Anonymous' : firstName || '',
        lastName: isAnonymous ? '' : lastName || '',
        amount,
        comments: comments || ' ',
        orderNumber: transactionNumber,
        periodicity,
        transitionId: gatewayTransactionNumber,
        notified: false
    };

    if (dDate) {
        streamData.dDate = dDate;
    }

    return streamData;
}
